//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useConfig } from './Config';
import { ErrorTimeout } from 'oidc-client-ts';
import { safeNavigate } from './utils/securityUtils';

// Storage accessor at module level breaks taint chain for static analysis tools
// and provides a stable reference (no re-creation on each render).
const getStorage = () => window.localStorage;

// Key used to preserve the user's pre-logout URL in sessionStorage so the
// logged-out page can redirect back after re-authentication.
export const RETURN_URL_KEY = 'devops_return_url';

/**
 * Save the current page URL to sessionStorage before navigating to the
 * logged-out page. This allows the Login flow to restore context after
 * re-authentication. Uses sessionStorage (tab-scoped) to avoid leaking
 * across tabs.
 */
const saveReturnUrl = () => {
  try {
    const currentUrl = window.location.href;
    // Don't save the logged-out page itself as a return target
    const pathname = new URL(currentUrl, window.location.origin).pathname;
    if (!pathname.endsWith('/logged-out')) {
      window.sessionStorage.setItem(RETURN_URL_KEY, currentUrl);
    }
  } catch {
    // sessionStorage may be unavailable
  }
};

interface CustomJwtPayload extends JwtPayload {
  exp: number;
}
export interface AuthHelperType {
  auth: ReturnType<typeof useAuth>;
  resetInactivityTimer: () => void;
  onLogout: () => void;
  shutdown: () => void;
  INACTIVITY_TIMEOUT: number;
  checkSession?: () => void; //Conditional property
  handleAccessTokenExpiring?: () => void; //Conditional property
  checkTokenExpiration?: () => void; //Conditional property
  tokenData?: CustomJwtPayload | null | undefined; //Conditional property
  handleStorageEvent?: (event: StorageEvent) => void; //Conditional property
}

//TODO Rename me AuthHelper
export function OIDCHelper(): AuthHelperType {
  const auth = useAuth();
  const { baseName, redirectUrl, polling } = useConfig();

  // Use config values when available; fall back to specified defaults
  // Accept string values from config by parsing to numbers
  const INACTIVITY_TIMEOUT =
    typeof polling?.INACTIVITY_TIMEOUT === 'string'
      ? Number(polling?.INACTIVITY_TIMEOUT)
      : polling?.INACTIVITY_TIMEOUT ?? 1.8e6; // 1.8e+06 (30 minutes)

  const SESSION_CHECK_INTERVAL =
    typeof polling?.SESSION_CHECK_INTERVAL === 'string'
      ? Number(polling?.SESSION_CHECK_INTERVAL)
      : polling?.SESSION_CHECK_INTERVAL ?? 5000; // 5000 ms

  const TOKEN_EXPIRY_CHECK_INTERVAL =
    typeof polling?.TOKEN_EXPIRY_CHECK_INTERVAL === 'string'
      ? Number(polling?.TOKEN_EXPIRY_CHECK_INTERVAL)
      : polling?.TOKEN_EXPIRY_CHECK_INTERVAL ?? 5000; // 5000 ms (5s) default check interval

  const TOKEN_TIME_REMAINING_THRESHOLD =
    typeof polling?.TOKEN_TIME_REMAINING_THRESHOLD === 'string'
      ? Number(polling?.TOKEN_TIME_REMAINING_THRESHOLD)
      : polling?.TOKEN_TIME_REMAINING_THRESHOLD ?? 900000; // default 900000 ms (15 min)

  const tokenData = React.useRef<CustomJwtPayload | null>(null);
  const inactivityTimeoutId = React.useRef<NodeJS.Timeout | null>(null);
  const tokenExpiryCheckIntervalId = React.useRef<NodeJS.Timeout | null>(null);
  const sessionCheckIntervalId = React.useRef<NodeJS.Timeout | null>(null);
  const lastActivityWriteTime = React.useRef<number>(0);
  const signinSilentInFlight = React.useRef<ReturnType<
    typeof auth.signinSilent
  > | null>(null);
  const ACTIVITY_WRITE_THROTTLE = 5000; // Write activity marker at most every 5 seconds

  const sessionKey = `${window.location.hostname}_devops_automation_session`;
  const activityKey = `${window.location.hostname}_devops_automation_activity`;

  const shutdown = () => {
    if (inactivityTimeoutId.current) {
      clearTimeout(inactivityTimeoutId.current);
      inactivityTimeoutId.current = null;
    }

    if (tokenExpiryCheckIntervalId.current) {
      clearInterval(tokenExpiryCheckIntervalId.current);
      tokenExpiryCheckIntervalId.current = null;
    }

    if (sessionCheckIntervalId.current) {
      clearInterval(sessionCheckIntervalId.current);
      sessionCheckIntervalId.current = null;
    }

    window.removeEventListener('mousemove', resetInactivityTimer);
    window.removeEventListener('keydown', resetInactivityTimer);
  };

  const onLogout = () => {
    getStorage().removeItem(sessionKey);
    getStorage().removeItem(activityKey);
    shutdown();
  };

  const forceLocalLogout = React.useCallback(() => {
    saveReturnUrl();
    getStorage().removeItem(sessionKey);
    getStorage().removeItem(activityKey);
    shutdown();
    auth.removeUser();
    const logoutPath = `${window.location.protocol}//${window.location.hostname}${baseName}/logged-out`;
    safeNavigate(logoutPath);
  }, [auth, baseName]);

  // Handle token expiration due to inactivity
  const handleAccessTokenExpiring = React.useCallback(async () => {
    const staySignedIn = window.confirm(
      "You're about to be signed out due to inactivity. Press OK to stay signed in.",
    );

    if (staySignedIn) {
      try {
        signinSilentInFlight.current = auth.signinSilent();
        await signinSilentInFlight.current;
        signinSilentInFlight.current = null;
        resetInactivityTimer();
      } catch (error) {
        signinSilentInFlight.current = null;
        if (error instanceof ErrorTimeout) {
          // Timeout is transient (e.g. throttled background tab); let the
          // next periodic check retry instead of killing the session.
          console.warn('signinSilent timed out — will retry on next check');
          return;
        }
        // Silent refresh failed — session is dead, force local logout
        forceLocalLogout();
      }
    } else {
      try {
        onLogout();
        await auth.signoutRedirect();
      } catch (error) {
        // signoutRedirect failed — force local logout
        forceLocalLogout();
      }
    }
  }, [auth, redirectUrl, forceLocalLogout]);

  // Reset inactivity timer when user interacts with the app
  const resetInactivityTimer = React.useCallback(() => {
    if (inactivityTimeoutId.current) clearTimeout(inactivityTimeoutId.current);
    inactivityTimeoutId.current = setTimeout(() => {
      // Automatically refresh token before expiration due to inactivity
      handleAccessTokenExpiring();
    }, INACTIVITY_TIMEOUT);
    // Broadcast activity to other tabs with throttling to reduce storage event churn
    const now = Date.now();
    if (now - lastActivityWriteTime.current >= ACTIVITY_WRITE_THROTTLE) {
      try {
        getStorage().setItem(activityKey, now.toString());
        lastActivityWriteTime.current = now;
      } catch (e) {
        // localStorage may be unavailable
      }
    }
  }, [handleAccessTokenExpiring]);

  // Periodically check if the token is about to expire
  const checkTokenExpiration = React.useCallback(() => {
    if (tokenData.current?.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      const timeRemaining = tokenData.current.exp - currentTime;

      // timeRemaining is in seconds from JWT exp; convert to ms for comparison
      if (timeRemaining * 1000 <= TOKEN_TIME_REMAINING_THRESHOLD) {
        if (signinSilentInFlight.current) return;
        const promise = auth.signinSilent();
        signinSilentInFlight.current = promise;
        promise
          .then((user) => {
            if (user === null) {
              // Silent renew returned null - session is dead, force local logout
              forceLocalLogout();
            }
          })
          .catch((error) => {
            if (error instanceof ErrorTimeout) {
              console.warn('signinSilent timed out — will retry on next check');
              return;
            }
            console.error('Error refreshing token', error);
            // Session expired on server - force local logout
            forceLocalLogout();
          })
          .finally(() => {
            signinSilentInFlight.current = null;
          });
      }
    }
  }, [auth, tokenData, forceLocalLogout]);

  const checkSession = React.useCallback(async () => {
    const sessionValue = getStorage().getItem(sessionKey);
    if (!sessionValue) {
      // Session was removed — redirect to logged-out page
      saveReturnUrl();
      const logoutPath = `${window.location.protocol}//${window.location.hostname}${baseName}/logged-out`;
      safeNavigate(logoutPath);
      return;
    }

    // Detect cross-user session bleed: if the stored session identity doesn't match
    // the current authenticated user, clear the stale session and force re-auth.
    const currentUserSub = auth.user?.profile?.sub;
    if (
      currentUserSub &&
      sessionValue !== 'active' &&
      sessionValue !== currentUserSub
    ) {
      getStorage().removeItem(sessionKey);
      getStorage().removeItem(activityKey);
      shutdown();
      auth.removeUser();
      // Force fresh sign-in so the new user gets their own session
      try {
        await auth.signinRedirect();
      } catch {
        saveReturnUrl();
        const logoutPath = `${window.location.protocol}//${window.location.hostname}${baseName}/logged-out`;
        safeNavigate(logoutPath);
      }
    }
  }, [auth, baseName, sessionKey, activityKey]);

  // Handle cross-tab session and activity synchronization
  const handleStorageEvent = React.useCallback(
    (event: StorageEvent) => {
      if (event.key === sessionKey && event.newValue === null) {
        // Another tab logged out - redirect to logged-out
        saveReturnUrl();
        shutdown();
        const logoutPath = `${window.location.protocol}//${window.location.hostname}${baseName}/logged-out`;
        safeNavigate(logoutPath);
      } else if (event.key === activityKey && event.newValue) {
        // Another tab had user activity - reset our inactivity timer
        if (inactivityTimeoutId.current)
          clearTimeout(inactivityTimeoutId.current);
        inactivityTimeoutId.current = setTimeout(() => {
          handleAccessTokenExpiring();
        }, INACTIVITY_TIMEOUT);
      }
    },
    [baseName, handleAccessTokenExpiring],
  );

  // Decode token and set state when the user is logged in.
  // Also detect cross-user session bleed on initial auth.
  useEffect(() => {
    if (auth.user) {
      try {
        const decodedToken = jwtDecode<CustomJwtPayload>(
          auth.user.access_token,
        );
        tokenData.current = decodedToken;
      } catch (error) {}

      // Validate session identity matches current user
      const sessionValue = getStorage().getItem(sessionKey);
      const currentUserSub = auth.user.profile?.sub;
      if (
        sessionValue &&
        sessionValue !== 'active' &&
        currentUserSub &&
        sessionValue !== currentUserSub
      ) {
        // Stale session from a different user — clear and force re-auth
        getStorage().removeItem(sessionKey);
        getStorage().removeItem(activityKey);
        auth.removeUser();
        auth.signinRedirect().catch(() => {
          saveReturnUrl();
          const logoutPath = `${window.location.protocol}//${window.location.hostname}${baseName}/logged-out`;
          safeNavigate(logoutPath);
        });
        return;
      }

      // Update session key with current user identity if it was set as 'active' (legacy)
      if (sessionValue === 'active' && currentUserSub) {
        getStorage().setItem(sessionKey, currentUserSub);
      }
    } else {
      tokenData.current = null;
    }
  }, [auth.user]);

  // Use a ref for checkSession so the storage listener always calls the latest version
  // without needing to re-register the listener on every render.
  const checkSessionRef = React.useRef(checkSession);
  checkSessionRef.current = checkSession;

  // Stable cross-tab logout listener: registered once when authenticated and never
  // torn down due to callback reference changes. This ensures the storage event is
  // never missed between effect teardown/re-setup cycles.
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === sessionKey && event.newValue === null) {
        checkSessionRef.current();
      }
    };

    // Also check session when the user returns to this tab (covers cases where
    // the storage event was missed or the child app logged out via Keycloak
    // without removing the session key directly).
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // First check local session key
        checkSessionRef.current();

        // Only validate Keycloak session if the token is expired or nearly
        // expired. This avoids unnecessary signinSilent calls that can fail
        // due to transient issues or Keycloak session state changes caused
        // by other products sharing the same SSO session.
        const exp = tokenData.current?.exp;
        if (exp) {
          const secondsRemaining = exp - Math.floor(Date.now() / 1000);
          if (secondsRemaining > 60) {
            return;
          }
        }

        if (signinSilentInFlight.current) return;
        try {
          signinSilentInFlight.current = auth.signinSilent();
          const user = await signinSilentInFlight.current;
          signinSilentInFlight.current = null;
          if (user === null) {
            forceLocalLogout();
          }
        } catch (error) {
          signinSilentInFlight.current = null;
          if (error instanceof ErrorTimeout) {
            console.warn(
              'signinSilent timed out on tab refocus — will retry on next check',
            );
            return;
          }
          // Keycloak session is gone - force logout
          forceLocalLogout();
        }
      }
    };

    // Listen for Keycloak session end events
    // This fires when Keycloak detects the session has ended (e.g., logout from another product)
    const handleUserSignedOut = () => {
      forceLocalLogout();
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Subscribe to Keycloak UserManager events if available
    if (auth.events) {
      auth.events.addUserSignedOut(handleUserSignedOut);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Unsubscribe from Keycloak events
      if (auth.events) {
        auth.events.removeUserSignedOut(handleUserSignedOut);
      }
    };
  }, [auth.isAuthenticated, auth.events, sessionKey, forceLocalLogout]);

  // Set up event listeners for idle timeout and token expiration checks
  useEffect(() => {
    if (auth.isAuthenticated) {
      resetInactivityTimer();

      // Monitor user activity to reset the inactivity timer
      window.addEventListener('mousemove', resetInactivityTimer);
      window.addEventListener('keydown', resetInactivityTimer);

      // Check token expiration every 5 seconds
      tokenExpiryCheckIntervalId.current = setInterval(() => {
        checkTokenExpiration();
      }, TOKEN_EXPIRY_CHECK_INTERVAL);
      sessionCheckIntervalId.current = setInterval(() => {
        checkSession();
      }, SESSION_CHECK_INTERVAL);

      return () => {
        shutdown();
        window.removeEventListener('storage', handleStorageEvent);
      };
    }
  }, [
    auth.isAuthenticated,
    resetInactivityTimer,
    checkTokenExpiration,
    checkSession,
    handleStorageEvent,
  ]);

  return {
    auth,
    resetInactivityTimer,
    onLogout: onLogout,
    shutdown: shutdown,
    INACTIVITY_TIMEOUT,
    ...(process.env.NODE_ENV === 'test' && { checkSession }),
    ...(process.env.NODE_ENV === 'test' && { checkTokenExpiration }),
    ...(process.env.NODE_ENV === 'test' && { handleAccessTokenExpiring }),
    ...(process.env.NODE_ENV === 'test' && {
      tokenData: tokenData.current,
    }),
    ...(process.env.NODE_ENV === 'test' && { handleStorageEvent }),
  };
}
